import React, { useState, useEffect, useMemo } from 'react';
import { Alert, AlertDescription, Button } from '@roger/ui-components';
import { AlertTriangle, Settings } from 'lucide-react';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import { FeatureFlag, PermissionSlug } from '../constants/featureFlags';
import { useToast } from '../hooks/useToastContext';
import { resyncWorksheetGoals } from '../services/rogerDocApi';
import GoalsSection from '../components/worksheetGoals/GoalsSection';
import GoalsProgressConfigModal from '../components/GoalsProgressConfigModal';

const WorksheetGoals: React.FC = () => {
  const {
    hasFeatureFlag,
    hasPermission,
    user,
    agencyClinics,
    agencyClinicsLoading,
    agencyClinicsLastFetched,
    fetchAgencyClinics,
  } = useStore(
    useShallow(state => ({
      hasFeatureFlag: state.hasFeatureFlag,
      hasPermission: state.hasPermission,
      user: state.user,
      agencyClinics: state.agencyClinics,
      agencyClinicsLoading: state.agencyClinicsLoading,
      agencyClinicsLastFetched: state.agencyClinicsLastFetched,
      fetchAgencyClinics: state.fetchAgencyClinics,
    }))
  );
  const [selectedClinicKey, setSelectedClinicKey] = useState<string | null>(
    null
  );
  const [isResyncing, setIsResyncing] = useState(false);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const { success, error } = useToast();

  const hasAccess =
    hasFeatureFlag(FeatureFlag.ADMIN_WORKSHEET_GOALS) &&
    hasPermission(PermissionSlug.GOALS_ADMIN_TAB);

  // Load the agency's own clinics. Every clinic (Kinnser-synced and
  // RogerEMR-native) now carries a clinic_key that RogerDoc matches against its
  // WellskyWorksheetGoal library, so sourcing the selector from the EMR clinics
  // lets native clinics manage goals too. RogerDoc's clinic list only knows
  // Kinnser-synced clinics, so it cannot drive this selector on its own.
  useEffect(() => {
    if (!hasAccess || !user?.agency?.id) {
      return;
    }
    const agencyId = user.agency.id;
    let cancelled = false;
    void (async () => {
      await fetchAgencyClinics(agencyId);
      // Toast only when the load actually left us with no clinics. Reading the
      // store after the await (instead of watching agencyClinicsError
      // reactively) and requiring an empty list avoids flashing a stale error
      // left by a prior page visit when the cache already holds valid clinics.
      const { agencyClinicsError, agencyClinics } = useStore.getState();
      if (!cancelled && agencyClinicsError && agencyClinics.length === 0) {
        error('Error', 'Failed to load clinics');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasAccess, user?.agency?.id, fetchAgencyClinics, error]);

  const agencyClinicKeys = useMemo(
    () =>
      agencyClinics
        .filter(clinic => !!clinic.clinic_key)
        .map(clinic => ({
          value: clinic.clinic_key as string,
          label: clinic.name,
        })),
    [agencyClinics]
  );

  const clinicsReady =
    agencyClinicsLastFetched !== null && !agencyClinicsLoading;

  // Pick an initial clinic once the clinics have loaded. Agencies with no
  // clinic_key clinics fall back to "All Clinics" so the tab still loads goals.
  useEffect(() => {
    if (selectedClinicKey || !clinicsReady) {
      return;
    }
    setSelectedClinicKey(
      agencyClinicKeys.length > 0 ? agencyClinicKeys[0].value : 'all_clinics'
    );
  }, [clinicsReady, agencyClinicKeys, selectedClinicKey]);

  if (!hasAccess) {
    return (
      <div className='container mx-auto px-4 py-6'>
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800 flex items-center gap-2'>
            <AlertTriangle className='h-4 w-4 text-red-600' />
            You don't have permission to access Worksheet Goals features. Please
            contact your administrator if you need access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Handle resync button click
  const handleResync = async () => {
    if (!user?.agency?.system_name) {
      error(
        'Resync Failed',
        'No agency found. Please ensure you are logged in.'
      );
      return;
    }

    setIsResyncing(true);
    try {
      await resyncWorksheetGoals();
      setRefetchTrigger(prev => prev + 1);
      success('Sync Complete', 'Goals synced successfully from Wellsky!');
    } catch (err) {
      console.error('Failed to resync worksheet goals:', err);
      error('Sync Failed', 'Failed to sync goals. Please try again.');
    } finally {
      setIsResyncing(false);
    }
  };

  // Prepare options for the dropdown
  const clinicKeyOptions = [
    ...agencyClinicKeys,
    {
      value: 'all_clinics',
      label: 'All Clinics',
    },
  ];

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex justify-between items-start'>
        <div>
          <h1 className='text-2xl font-bold'>Goals and Interventions</h1>
          <p className='text-muted-foreground text-sm mt-1'>
            You can review the goals from your Wellsky library and select which
            ones should be required to be automatically added for{' '}
            <strong>all users and all patients</strong> of that speciality
          </p>
        </div>

        <Button
          variant='outline'
          size='sm'
          onClick={() => setIsConfigModalOpen(true)}
          className='flex items-center gap-2'
        >
          <Settings className='h-4 w-4' />
          Progress Tracking Settings
        </Button>
      </div>

      {/* Goals Section */}
      <GoalsSection
        clinicKey={selectedClinicKey}
        onClinicKeyChange={setSelectedClinicKey}
        clinicKeyOptions={clinicKeyOptions}
        showClinicSelector={agencyClinicKeys.length > 1}
        agencyName={user?.agency?.written_name || user?.agency?.system_name}
        onResync={handleResync}
        isResyncing={isResyncing}
        isLoadingClinics={!clinicsReady}
        canResync={!!user?.agency?.system_name}
        // The "Resync from Wellsky" button only makes sense for Kinnser
        // agencies; hide it for non-Kinnser agencies (e.g. TSC).
        showResync={user?.agency?.is_kinnser_enabled === true}
        refetchTrigger={refetchTrigger}
      />

      {/* Config Modal */}
      <GoalsProgressConfigModal
        open={isConfigModalOpen}
        onOpenChange={setIsConfigModalOpen}
      />
    </div>
  );
};

export default WorksheetGoals;
